/**
 * ExcelImportModal — Importa casos de prueba desde un archivo Excel.
 *
 * Formato esperado (una fila por paso):
 *   titulo | descripcion | estado | tags | paso_orden | paso_descripcion | paso_resultado_esperado
 *
 * Flujo:
 * 1. Usuario selecciona .xlsx
 * 2. SheetJS parsea en browser (sin subir al servidor)
 * 3. Se muestra vista previa con validación
 * 4. Al confirmar: POST /scenarios/{id}/cases + POST /cases/{id}/steps
 */
import React, { useState, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { cases as casesApi, steps as stepsApi } from '../../services/api';

// ─── Types ──────────────────────────────────────────────────────────────────

interface ParsedStep {
  order_index: number;
  description: string;
  expected_result: string;
}

interface ParsedCase {
  title: string;
  description: string;
  status: 'draft' | 'active' | 'deprecated';
  tags: string[];
  criticality: string;
  preconditions: string;
  postconditions: string;
  steps: ParsedStep[];
  rowStart: number;
  errors: string[];
}

interface ImportResult {
  title: string;
  success: boolean;
  error?: string;
  steps_created: number;
}

// ─── Excel template download ─────────────────────────────────────────────────

function downloadTemplate() {
  const wb = XLSX.utils.book_new();
  const rows = [
    // Header
    ['titulo', 'descripcion', 'estado', 'tags', 'criticidad', 'precondiciones', 'postcondiciones', 'paso_orden', 'paso_descripcion', 'paso_resultado_esperado'],
    // Example case 1 — 3 steps
    ['Login exitoso con credenciales válidas', 'Verifica que un usuario con credenciales válidas puede autenticarse', 'active', 'smoke, happy-path', 'high', 'El usuario debe estar registrado en el sistema', 'El usuario queda autenticado y con sesión activa', 1, 'Navegar a la página de login', 'Se muestra el formulario con campos email y contraseña'],
    ['', '', '', '', '', '', '', 2, 'Ingresar email y contraseña válidos', 'Los campos aceptan el input sin errores'],
    ['', '', '', '', '', '', '', 3, 'Hacer clic en el botón Iniciar sesión', 'El usuario es redirigido al dashboard'],
    // Example case 2 — 2 steps
    ['Login fallido con contraseña incorrecta', 'Verifica que el sistema rechaza credenciales inválidas', 'draft', 'regression, error-handling', 'medium', 'El usuario debe tener cuenta registrada', 'El sistema no crea sesión y muestra mensaje de error', 1, 'Navegar a la página de login', 'Se muestra el formulario'],
    ['', '', '', '', '', '', '', 2, 'Ingresar contraseña incorrecta y hacer clic en Iniciar sesión', 'Se muestra mensaje de error "Credenciales inválidas"'],
  ];

  const ws = XLSX.utils.aoa_to_sheet(rows);

  // Column widths
  ws['!cols'] = [
    { wch: 40 }, { wch: 40 }, { wch: 10 }, { wch: 22 },
    { wch: 10 }, { wch: 35 }, { wch: 35 },
    { wch: 10 }, { wch: 40 }, { wch: 40 },
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'Casos de Prueba');
  XLSX.writeFile(wb, 'plantilla_casos_de_prueba.xlsx');
}

// ─── Parser ──────────────────────────────────────────────────────────────────

const VALID_STATUSES = new Set(['draft', 'active', 'deprecated']);

function parseExcel(file: File): Promise<ParsedCase[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows: string[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' }) as string[][];

        if (rows.length < 2) { resolve([]); return; }

        // Skip header row
        const dataRows = rows.slice(1);
        const cases: ParsedCase[] = [];
        let currentCase: ParsedCase | null = null;

        dataRows.forEach((row, rowIdx) => {
          const [
            titulo = '', descripcion = '', estado = '', tags = '',
            criticidad = '', precondiciones = '', postcondiciones = '',
            paso_orden = '', paso_desc = '', paso_resultado = ''
          ] = row.map(v => String(v ?? '').trim());

          const hasTitle = titulo.replace(/\s+/g, '').length > 0;
          const hasStep = paso_desc !== '';

          // New case row
          if (hasTitle) {
            const VALID_CRITICALITY = new Set(['critical', 'high', 'medium', 'low']);
            currentCase = {
              title: titulo.slice(0, 512),
              description: descripcion,
              status: VALID_STATUSES.has(estado.toLowerCase())
                ? estado.toLowerCase() as 'draft' | 'active' | 'deprecated'
                : 'draft',
              tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
              criticality: VALID_CRITICALITY.has(criticidad.toLowerCase()) ? criticidad.toLowerCase() : 'medium',
              preconditions: precondiciones,
              postconditions: postcondiciones,
              steps: [],
              rowStart: rowIdx + 2, // 1-indexed, +1 for header
              errors: [],
            };
            if (!titulo.trim()) currentCase.errors.push('Título requerido');
            cases.push(currentCase);
          }

          // Step row (belongs to currentCase)
          if (hasStep && currentCase) {
            const order = parseInt(String(paso_orden)) || currentCase.steps.length + 1;
            currentCase.steps.push({
              order_index: order - 1,
              description: paso_desc.slice(0, 1024),
              expected_result: paso_resultado,
            });
          }
        });

        const finalCases = cases.filter(c => c.title);
        console.log('[ExcelImport] Total rows:', rows.length, 'Parsed cases:', finalCases.length,
          finalCases.map(c => ({ title: c.title, steps: c.steps.length, errors: c.errors })));
        resolve(finalCases);
      } catch (err) {
        reject(new Error('No se pudo leer el archivo. Asegúrate de que es un .xlsx válido.'));
      }
    };
    reader.onerror = () => reject(new Error('Error al leer el archivo.'));
    reader.readAsArrayBuffer(file);
  });
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const OVERLAY: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
  zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const MODAL: React.CSSProperties = {
  width: 680, maxHeight: '88vh', borderRadius: 16,
  background: 'var(--color-background-primary)',
  border: '1px solid var(--color-border-secondary)',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};
const BTN_PRIMARY: React.CSSProperties = {
  padding: '9px 20px', borderRadius: 8, border: 'none',
  background: '#3B8BD4', color: '#fff', cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};
const BTN_SECONDARY: React.CSSProperties = {
  padding: '9px 16px', borderRadius: 8,
  border: '1px solid var(--color-border-secondary)',
  background: 'transparent', cursor: 'pointer',
  fontSize: 13, color: 'var(--color-text-secondary)',
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface ExcelImportModalProps {
  scenarioId: string;
  scenarioTitle: string;
  onClose: () => void;
  onImported: () => void;
}

export function ExcelImportModal({ scenarioId, scenarioTitle, onClose, onImported }: ExcelImportModalProps) {
  const [step, setStep] = useState<'upload' | 'preview' | 'importing' | 'done'>('upload');
  const [parsedCases, setParsedCases] = useState<ParsedCase[]>([]);
  const [results, setResults] = useState<ImportResult[]>([]);
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(async (file: File) => {
    if (!file.name.match(/\.xlsx?$/i)) {
      setError('Solo se aceptan archivos .xlsx o .xls');
      return;
    }
    setError('');
    try {
      const parsed = await parseExcel(file);
      if (parsed.length === 0) {
        setError('No se encontraron casos en el archivo. Verifica el formato.');
        return;
      }
      setParsedCases(parsed);
      setStep('preview');
    } catch (e: unknown) {
      setError((e as Error).message);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleImport = async () => {
    setImporting(true);
    setStep('importing');
    const results: ImportResult[] = [];

    for (let caseIdx = 0; caseIdx < parsedCases.length; caseIdx++) {
      const pc = parsedCases[caseIdx];
      console.log('[ExcelImport] Processing case:', pc.title, 'steps:', pc.steps.length, 'errors:', pc.errors);
      if (pc.errors.length > 0) {
        results.push({ title: pc.title, success: false, error: pc.errors.join(', '), steps_created: 0 });
        continue;
      }
      try {
        const newCase = await casesApi.create(scenarioId, {
          title: pc.title,
          description: pc.description,
          status: pc.status,
          tags: pc.tags,
          order_index: caseIdx,
          criticality: pc.criticality as 'critical' | 'high' | 'medium' | 'low',
          preconditions: pc.preconditions || undefined,
          postconditions: pc.postconditions || undefined,
        });
        console.log('[ExcelImport] Case created:', newCase.id, pc.title);

        let stepsCreated = 0;
        for (const step of pc.steps) {
          try {
            await stepsApi.create(newCase.id, {
              description: step.description,
              expected_result: step.expected_result || undefined,
              order_index: step.order_index,
            });
            stepsCreated++;
          } catch {
            // Step failed but case was created — continue
          }
        }

        results.push({ title: pc.title, success: true, steps_created: stepsCreated });
      } catch (e: unknown) {
        console.error('[ExcelImport] FAILED case:', pc.title, e);
        results.push({ title: pc.title, success: false, error: (e as Error).message, steps_created: 0 });
      }
    }

    setResults(results);
    setImporting(false);
    setStep('done');

    if (results.some(r => r.success)) {
      onImported();
    }
  };

  const validCases = parsedCases.filter(c => c.errors.length === 0);
  const invalidCases = parsedCases.filter(c => c.errors.length > 0);
  const successCount = results.filter(r => r.success).length;
  const failCount = results.filter(r => !r.success).length;

  return (
    <div style={OVERLAY} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={MODAL}>

        {/* Header */}
        <div style={{ padding: '16px 22px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}>Importar casos desde Excel</div>
            <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginTop: 2 }}>Escenario: {scenarioTitle}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {/* Step indicator */}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              {['Archivo', 'Vista previa', 'Resultado'].map((label, i) => {
                const current = step === 'upload' ? 0 : step === 'preview' ? 1 : 2;
                const done = i < current;
                const active = i === current;
                return (
                  <React.Fragment key={label}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <div style={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, background: done ? '#1D9E75' : active ? '#3B8BD4' : 'var(--color-background-secondary)', color: done || active ? '#fff' : 'var(--color-text-tertiary)', border: `1px solid ${done ? '#1D9E75' : active ? '#3B8BD4' : 'var(--color-border-secondary)'}` }}>
                        {done ? '✓' : i + 1}
                      </div>
                      <span style={{ fontSize: 11, color: active ? '#3B8BD4' : done ? '#1D9E75' : 'var(--color-text-tertiary)', fontWeight: active ? 600 : 400 }}>{label}</span>
                    </div>
                    {i < 2 && <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)' }}>›</span>}
                  </React.Fragment>
                );
              })}
            </div>
            <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: 'var(--color-text-tertiary)' }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>

          {/* ── STEP 1: Upload ─────────────────────────────────────────── */}
          {step === 'upload' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {/* Drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={e => e.preventDefault()}
                onClick={() => fileRef.current?.click()}
                style={{ border: '2px dashed var(--color-border-secondary)', borderRadius: 12, padding: '40px 20px', textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.2s, background 0.2s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#3B8BD4'; e.currentTarget.style.background = '#E6F1FB11'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--color-border-secondary)'; e.currentTarget.style.background = 'transparent'; }}
              >
                <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 6 }}>
                  Arrastra tu archivo Excel aquí
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                  o haz clic para seleccionar · Formatos: .xlsx, .xls
                </div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
              </div>

              {error && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FCEBEB', border: '1px solid #F7C1C1', color: '#791F1F', fontSize: 12 }}>
                  ⚠ {error}
                </div>
              )}

              {/* Format guide */}
              <div style={{ borderRadius: 12, border: '1px solid var(--color-border-tertiary)', overflow: 'hidden' }}>
                <div style={{ padding: '10px 14px', background: 'var(--color-background-secondary)', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text-secondary)' }}>FORMATO REQUERIDO</span>
                  <button onClick={downloadTemplate} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid var(--color-border-secondary)', background: 'transparent', cursor: 'pointer', fontSize: 11, color: '#3B8BD4', fontWeight: 600 }}>
                    📥 Descargar plantilla
                  </button>
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                    <thead>
                      <tr style={{ background: 'var(--color-background-secondary)' }}>
                        {['titulo *', 'descripcion', 'estado', 'tags', 'criticidad', 'precondiciones', 'postcondiciones', 'paso_orden', 'paso_descripcion', 'paso_resultado_esperado'].map(h => (
                          <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--color-text-secondary)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--color-border-tertiary)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Login exitoso', 'Verifica...', 'active', 'smoke', 'high', 'Usuario registrado', 'Sesión activa', '1', 'Navegar a /login', 'Se muestra el form'],
                        ['', '', '', '', '', '', '', '2', 'Ingresar datos válidos', 'Redirige al dashboard'],
                        ['Login fallido', '', 'draft', 'regression', 'medium', 'Usuario con cuenta', '', '1', 'Ingresar datos inválidos', 'Muestra error'],
                      ].map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
                          {row.map((cell, j) => (
                            <td key={j} style={{ padding: '5px 10px', color: cell ? 'var(--color-text-primary)' : 'var(--color-text-tertiary)', fontStyle: cell ? 'normal' : 'italic' }}>
                              {cell || '(vacío)'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ padding: '8px 14px', fontSize: 11, color: 'var(--color-text-tertiary)', background: 'var(--color-background-secondary)', borderTop: '1px solid var(--color-border-tertiary)' }}>
                  💡 Un caso puede tener múltiples pasos. Deja las columnas de caso vacías en las filas de pasos adicionales. Estado: <code>draft|active|deprecated</code>. Criticidad: <code>critical|high|medium|low</code>
                </div>
              </div>
            </div>
          )}

          {/* ── STEP 2: Preview ────────────────────────────────────────── */}
          {step === 'preview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Summary chips */}
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ padding: '4px 12px', borderRadius: 8, background: '#E1F5EE', color: '#0F6E56', fontSize: 12, fontWeight: 600 }}>
                  ✓ {validCases.length} caso{validCases.length !== 1 ? 's' : ''} válido{validCases.length !== 1 ? 's' : ''}
                </span>
                {invalidCases.length > 0 && (
                  <span style={{ padding: '4px 12px', borderRadius: 8, background: '#FCEBEB', color: '#A32D2D', fontSize: 12, fontWeight: 600 }}>
                    ⚠ {invalidCases.length} con error
                  </span>
                )}
                <span style={{ padding: '4px 12px', borderRadius: 8, background: 'var(--color-background-secondary)', color: 'var(--color-text-secondary)', fontSize: 12, fontWeight: 600 }}>
                  {parsedCases.reduce((acc, c) => acc + c.steps.length, 0)} pasos en total
                </span>
              </div>

              {/* Cases preview */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {parsedCases.map((pc, i) => (
                  <div key={i} style={{ borderRadius: 10, border: `1px solid ${pc.errors.length > 0 ? '#F7C1C1' : 'var(--color-border-tertiary)'}`, overflow: 'hidden' }}>
                    {/* Case header */}
                    <div style={{ padding: '10px 14px', background: pc.errors.length > 0 ? '#FCEBEB' : 'var(--color-background-secondary)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <span style={{ fontSize: 14, marginTop: 1 }}>{pc.errors.length > 0 ? '⚠' : '◻'}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)' }}>{pc.title}</div>
                        <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                          <span style={{ padding: '1px 6px', borderRadius: 4, background: 'var(--color-background-primary)', color: 'var(--color-text-tertiary)', fontSize: 10, border: '1px solid var(--color-border-tertiary)' }}>
                            {pc.status}
                          </span>
                          {pc.tags.map(t => (
                            <span key={t} style={{ padding: '1px 6px', borderRadius: 4, background: '#E6F1FB', color: '#185FA5', fontSize: 10 }}>{t}</span>
                          ))}
                          <span style={{ padding: '1px 6px', borderRadius: 4, background: 'var(--color-background-primary)', color: 'var(--color-text-tertiary)', fontSize: 10 }}>
                            {pc.steps.length} paso{pc.steps.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        {pc.errors.length > 0 && (
                          <div style={{ marginTop: 4, fontSize: 11, color: '#A32D2D' }}>
                            {pc.errors.join(' · ')}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Steps preview */}
                    {pc.steps.length > 0 && (
                      <div style={{ padding: '8px 14px 8px 38px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {pc.steps.map((step, j) => (
                          <div key={j} style={{ display: 'flex', gap: 8, fontSize: 11 }}>
                            <span style={{ color: 'var(--color-text-tertiary)', minWidth: 16 }}>{j + 1}.</span>
                            <span style={{ color: 'var(--color-text-secondary)' }}>{step.description}</span>
                            {step.expected_result && (
                              <span style={{ color: 'var(--color-text-tertiary)' }}>→ {step.expected_result.slice(0, 60)}{step.expected_result.length > 60 ? '...' : ''}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {invalidCases.length > 0 && (
                <div style={{ padding: '10px 14px', borderRadius: 8, background: '#FAEEDA', border: '1px solid #F5C97E', color: '#5F3B00', fontSize: 12 }}>
                  ⚠ Los casos con errores serán omitidos. Se importarán {validCases.length} caso{validCases.length !== 1 ? 's' : ''}.
                </div>
              )}
            </div>
          )}

          {/* ── STEP 3: Importing ──────────────────────────────────────── */}
          {step === 'importing' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: '40px 0' }}>
              <div style={{ fontSize: 36 }}>⟳</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)' }}>Importando casos...</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>
                Creando {validCases.length} caso{validCases.length !== 1 ? 's' : ''} con sus pasos
              </div>
            </div>
          )}

          {/* ── STEP 4: Done ───────────────────────────────────────────── */}
          {step === 'done' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Summary */}
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>{failCount === 0 ? '✅' : successCount === 0 ? '❌' : '⚠️'}</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 6 }}>
                  {failCount === 0 ? 'Importación completada' : successCount === 0 ? 'Importación fallida' : 'Importación parcial'}
                </div>
                <div style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>
                  {successCount > 0 && `${successCount} caso${successCount !== 1 ? 's' : ''} importado${successCount !== 1 ? 's' : ''} correctamente`}
                  {failCount > 0 && ` · ${failCount} con error`}
                </div>
              </div>

              {/* Results list */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {results.map((r, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 8, background: r.success ? '#E1F5EE' : '#FCEBEB', border: `1px solid ${r.success ? '#9DE3C7' : '#F7C1C1'}` }}>
                    <span style={{ fontSize: 14 }}>{r.success ? '✓' : '✗'}</span>
                    <span style={{ flex: 1, fontSize: 12, color: 'var(--color-text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.title}</span>
                    {r.success && (
                      <span style={{ fontSize: 11, color: '#0F6E56' }}>{r.steps_created} paso{r.steps_created !== 1 ? 's' : ''}</span>
                    )}
                    {!r.success && (
                      <span style={{ fontSize: 11, color: '#A32D2D' }}>{r.error?.slice(0, 40)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 22px', borderTop: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 10, justifyContent: 'flex-end', background: 'var(--color-background-primary)' }}>
          {step === 'upload' && (
            <button onClick={onClose} style={BTN_SECONDARY}>Cancelar</button>
          )}
          {step === 'preview' && (
            <>
              <button onClick={() => { setStep('upload'); setParsedCases([]); setError(''); }} style={BTN_SECONDARY}>
                ← Cambiar archivo
              </button>
              <button
                onClick={handleImport}
                disabled={validCases.length === 0}
                style={{ ...BTN_PRIMARY, opacity: validCases.length === 0 ? 0.5 : 1, cursor: validCases.length === 0 ? 'not-allowed' : 'pointer' }}
              >
                ✓ Importar {validCases.length} caso{validCases.length !== 1 ? 's' : ''}
              </button>
            </>
          )}
          {step === 'done' && (
            <button onClick={onClose} style={BTN_PRIMARY}>Cerrar</button>
          )}
        </div>
      </div>
    </div>
  );
}